/**
 * API SDK - Centralized API Client
 *
 * ALL API calls to the backend MUST go through this SDK.
 * This ensures consistent authentication, token refresh, and 401 handling.
 *
 * Features:
 * - Automatic token refresh via Auth0's getAccessTokenSilently
 * - Automatic logout on session expiry
 * - Deduplication of concurrent refresh attempts
 * - Support for mock mode (development)
 * - Type-safe request methods
 * - Standardized error handling with ApiClientError
 */

import { APP_CONFIG } from '@/config/app.config';
import { ApiResponse, ApiError } from '../types/api-response';
import { ApiClientError } from './errors';
import { isSessionExpiryError } from '@/auth/auth0-config';

// Types for Auth0 integration
type GetTokenOptions = { cacheMode?: 'on' | 'off' };
type GetTokenFn = (options?: GetTokenOptions) => Promise<string>;
type LogoutFn = () => Promise<void>;

interface SdkConfig {
  getAccessTokenSilently: GetTokenFn;
  logout: LogoutFn;
}

class ApiSdk {
  private config: SdkConfig | null = null;
  private mockMode = false;
  private isLoggingOut = false;
  private refreshPromise: Promise<string> | null = null;

  // Per-endpoint circuit breaker. A buggy component (e.g. a remount loop) or a
  // degraded backend must not be able to hammer an endpoint indefinitely — that
  // is how a single stuck tab turned into 1M+ requests overnight. After
  // CIRCUIT_THRESHOLD consecutive failures for a given method+path, the circuit
  // opens and further calls fail fast (no network) until a backoff elapses.
  private circuits = new Map<string, { failures: number; openUntil: number }>();
  private static readonly CIRCUIT_THRESHOLD = 6;
  private static readonly CIRCUIT_BASE_MS = 5_000;
  private static readonly CIRCUIT_MAX_MS = 60_000;

  /**
   * Initialize the SDK with Auth0 methods.
   * Must be called after successful authentication.
   */
  initialize(config: SdkConfig): void {
    this.config = config;
    this.mockMode = false;
    this.isLoggingOut = false;
    console.log('[API SDK] Initialized with Auth0');
  }

  /**
   * Initialize SDK for mock mode (development without Auth0)
   */
  initializeMockMode(): void {
    this.mockMode = true;
    this.config = null;
    this.isLoggingOut = false;
    console.log('[API SDK] Initialized in mock mode');
  }

  /**
   * Reset the SDK state (called on logout)
   */
  reset(): void {
    this.config = null;
    this.mockMode = false;
    this.isLoggingOut = false;
    this.refreshPromise = null;
  }

  /**
   * Check if SDK is ready to make authenticated requests
   */
  isReady(): boolean {
    return this.config !== null || this.mockMode;
  }

  /**
   * Handle session expiry - clear tokens and logout
   */
  private async handleSessionExpiry(): Promise<never> {
    // Prevent multiple logout attempts
    if (this.isLoggingOut) {
      throw new Error('Session expired');
    }

    this.isLoggingOut = true;
    console.warn('[API SDK] Session expired - logging out');

    // Clear all auth data from localStorage
    localStorage.removeItem('auth0_access_token');
    localStorage.removeItem('auth0_user');
    localStorage.removeItem('auth0_is_authenticated');

    // Trigger logout via Auth0
    if (this.config) {
      try {
        await this.config.logout();
      } catch {
        // If logout fails, redirect to login with return path
        const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/login?returnTo=${returnTo}`;
      }
    } else {
      // Mock mode - redirect to login with return path
      const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?returnTo=${returnTo}`;
    }

    throw new Error('Session expired');
  }

  /**
   * Get token for mock mode from localStorage
   */
  private getMockToken(): string {
    const token = localStorage.getItem('auth0_access_token');
    if (!token) {
      throw new Error('No authentication token available');
    }
    return token;
  }

  /**
   * Get a valid access token.
   * Uses Auth0's getAccessTokenSilently which automatically handles refresh.
   * Deduplicates concurrent refresh attempts.
   */
  private async getToken(forceRefresh = false): Promise<string> {
    // Mock mode - just use localStorage token
    if (this.mockMode) {
      return this.getMockToken();
    }

    if (!this.config) {
      throw new Error('API SDK not initialized. Ensure user is authenticated.');
    }

    // If already refreshing, wait for that to complete. Waiters must apply
    // the same session-expiry classification as the creator — otherwise a
    // login_required rejection surfaces to them as a raw Auth0 error.
    if (this.refreshPromise && !forceRefresh) {
      try {
        return await this.refreshPromise;
      } catch (error) {
        if (isSessionExpiryError(error)) {
          return this.handleSessionExpiry();
        }
        throw error;
      }
    }

    const options: GetTokenOptions | undefined = forceRefresh
      ? { cacheMode: 'off' }
      : undefined;

    // Create a shared promise for concurrent requests
    const refreshPromise = this.config.getAccessTokenSilently(options);
    this.refreshPromise = refreshPromise;

    try {
      return await refreshPromise;
    } catch (error) {
      if (isSessionExpiryError(error)) {
        return this.handleSessionExpiry();
      }
      throw error;
    } finally {
      // Only clear if it's still ours — a concurrent forceRefresh may have
      // replaced the shared promise, and nulling it here would break its dedup.
      if (this.refreshPromise === refreshPromise) {
        this.refreshPromise = null;
      }
    }
  }

  /**
   * Circuit-breaker key: method + path (query stripped so paginated calls
   * share one circuit).
   */
  private circuitKey(endpoint: string, options: RequestInit): string {
    const method = (options.method || 'GET').toUpperCase();
    const path = endpoint.split('?')[0];
    return `${method} ${path}`;
  }

  /**
   * Make an authenticated API request — the ONLY method for backend calls.
   * Wraps the real request with a per-endpoint circuit breaker so a runaway
   * caller or a failing endpoint can't loop forever.
   */
  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const key = this.circuitKey(endpoint, options);
    const circuit = this.circuits.get(key);
    if (circuit && Date.now() < circuit.openUntil) {
      console.warn('[API SDK] Circuit open — blocking request', key);
      throw new Error(`Circuit open for ${key} — too many recent failures; backing off`);
    }

    try {
      const result = await this.executeRequest<T>(endpoint, options);
      this.circuits.delete(key); // success resets the breaker
      return result;
    } catch (err) {
      // Validation errors (400/422) are user-fixable — a corrected resubmit is
      // legitimate, so they don't trip the breaker. Everything else counts:
      // 403/429/5xx/network are exactly the loop-prone failures this guards
      // against (the invites 403 storm was a 403 loop).
      if (err instanceof ApiClientError && err.isValidationError()) {
        throw err;
      }
      const failures = (this.circuits.get(key)?.failures ?? 0) + 1;
      const openUntil =
        failures >= ApiSdk.CIRCUIT_THRESHOLD
          ? Date.now() +
            Math.min(
              ApiSdk.CIRCUIT_MAX_MS,
              ApiSdk.CIRCUIT_BASE_MS * 2 ** (failures - ApiSdk.CIRCUIT_THRESHOLD),
            )
          : 0;
      this.circuits.set(key, { failures, openUntil });
      console.warn('[API SDK] Request failed', key, {
        failures,
        opensCircuit: openUntil > 0,
        status: err instanceof ApiClientError ? err.code : (err as Error)?.name,
      });
      throw err;
    }
  }

  /**
   * Execute the actual authenticated request (token, 401/403 handling).
   *
   * @param endpoint - API endpoint (e.g., '/api/domains')
   * @param options - Fetch options (method, body, headers, etc.)
   * @returns The response data (unwrapped from { data: T } if applicable)
   */
  private async executeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    if (!this.isReady()) {
      throw new Error('API SDK not initialized');
    }

    const url = endpoint.startsWith('http')
      ? endpoint
      : `${APP_CONFIG.API_BASE_URL}${endpoint}`;

    // Get token (Auth0 SDK handles refresh automatically)
    const token = await this.getToken();

    // For FormData bodies, omit the JSON Content-Type so the browser can set
    // the correct multipart boundary itself.
    const isFormData = options.body instanceof FormData;
    const baseHeaders: Record<string, string> = isFormData
      ? { Authorization: `Bearer ${token}` }
      : { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

    // Make the request
    const response = await fetch(url, {
      ...options,
      headers: {
        ...baseHeaders,
        ...options.headers,
      },
    });

    // Handle 401 - attempt ONE retry with forced token refresh
    // 401 = authentication failure (invalid/expired token)
    if (response.status === 401) {
      console.warn(`[API SDK] Received 401 - attempting token refresh`);

      try {
        const freshToken = await this.getToken(true);

        const retryHeaders: Record<string, string> = isFormData
          ? { Authorization: `Bearer ${freshToken}` }
          : { 'Content-Type': 'application/json', Authorization: `Bearer ${freshToken}` };

        const retryResponse = await fetch(url, {
          ...options,
          headers: {
            ...retryHeaders,
            ...options.headers,
          },
        });

        // Still 401 after refresh - session is truly expired
        if (retryResponse.status === 401) {
          return this.handleSessionExpiry();
        }

        return this.parseResponse<T>(retryResponse);
      } catch (error) {
        // If error is already "Session expired", rethrow
        if (error instanceof Error && error.message === 'Session expired') {
          throw error;
        }
        // Only a genuine Auth0 session-expiry error may log the user out. A
        // transient failure here (network blip, Auth0 rate limit) must
        // propagate as an error — logging out on it destroys unsaved work.
        if (isSessionExpiryError(error)) {
          return this.handleSessionExpiry();
        }
        throw error;
      }
    }

    // Handle 403 - could be authorization failure OR session expiry
    // 403 = forbidden (authenticated but not authorized, OR token issue in some Auth0 configs)
    // Try to parse response first - if it has a valid API error, it's permission denial, not session expiry
    if (response.status === 403) {
      try {
        const clonedResponse = response.clone();
        const json = await clonedResponse.json();

        // Invite-only mode gate: the backend refused to create a new `users`
        // row because INVITE_ONLY_MODE is on and the signup email has no
        // matching invitation. Route to the dedicated screen; do not retry.
        if (json?.error?.code === 'INVITE_ONLY_MODE' && window.location.pathname !== '/invite-only') {
          window.location.assign('/invite-only');
          return this.parseResponse<T>(response);
        }

        // If response has a proper error structure, it's an authorization error - let parseResponse handle it
        if (json && (json.error || json.message || json.success === false)) {
          console.warn(`[API SDK] Received 403 - authorization denied`);
          return this.parseResponse<T>(response);
        }
      } catch {
        // Couldn't parse response - might be Auth0/Cloudflare level 403
      }

      // No valid error body - treat as potential session issue, retry with fresh token
      console.warn(`[API SDK] Received 403 without API error body - attempting token refresh`);

      try {
        const freshToken = await this.getToken(true);

        const retryHeaders: Record<string, string> = isFormData
          ? { Authorization: `Bearer ${freshToken}` }
          : { 'Content-Type': 'application/json', Authorization: `Bearer ${freshToken}` };

        const retryResponse = await fetch(url, {
          ...options,
          headers: {
            ...retryHeaders,
            ...options.headers,
          },
        });

        // Still 403 after refresh - let it fall through to parseResponse
        if (retryResponse.status === 403) {
          return this.parseResponse<T>(retryResponse);
        }

        return this.parseResponse<T>(retryResponse);
      } catch (error) {
        if (error instanceof Error && error.message === 'Session expired') {
          throw error;
        }
        // Same rule as the 401 path: transient failures propagate; only a
        // genuine session-expiry error logs the user out.
        if (isSessionExpiryError(error)) {
          return this.handleSessionExpiry();
        }
        throw error;
      }
    }

    return this.parseResponse<T>(response);
  }

  /**
   * Parse the API response, handling errors and unwrapping data
   * Uses the standardized API response format
   */
  private async parseResponse<T>(response: Response): Promise<T> {
    // Edge responses (Cloudflare 429 "Too many requests", 5xx error pages, proxy
    // HTML) are NOT JSON. Read text first and parse defensively — a raw
    // response.json() throws a SyntaxError that masks the real status and breaks
    // every caller (one rate-limited request would otherwise crash the app).
    const text = await response.text();
    let json: ApiResponse<T>;
    try {
      json = (text ? JSON.parse(text) : {}) as ApiResponse<T>;
    } catch {
      throw new ApiClientError({
        code: response.status,
        type: response.status === 429 ? 'RATE_LIMITED' : 'NON_JSON_RESPONSE',
        message:
          response.status === 429
            ? 'Too many requests — please slow down and try again shortly.'
            : text.trim().slice(0, 200) || `API error: ${response.status}`,
      });
    }

    // Check for error response (success: false or HTTP error)
    if (!response.ok || json.success === false) {
      const raw = json as unknown as Record<string, unknown>;
      const rawError = raw.error;
      const rawMessage = typeof raw.message === 'string' ? raw.message : undefined;

      // Standardized shape: `error` is an ApiError object.
      if (rawError && typeof rawError === 'object') {
        throw new ApiClientError(rawError as ApiError);
      }

      // Legacy middleware shape: `{ error: 'Not Found', message: 'Site not found', code: 'SITE_NOT_FOUND' }`
      // `error` is a string (the type), `message` is the human-readable text.
      // Without this branch, `new ApiClientError(string)` produced an empty
      // message and surfaced the wrong text to the user.
      const message =
        rawMessage ||
        (typeof rawError === 'string' ? rawError : undefined) ||
        `API error: ${response.status}`;
      const type =
        typeof rawError === 'string' ? rawError : 'UNKNOWN_ERROR';

      throw new ApiClientError({
        code: response.status,
        type,
        message,
      });
    }

    // Success response - unwrap data
    // Handle both { success: true, data: T } and direct T responses
    if (json && typeof json === 'object' && 'data' in json && json.data !== undefined) {
      return json.data as T;
    }

    // Direct response (not wrapped in { data: T })
    return json as unknown as T;
  }
}

// Re-export ApiClientError for convenience
export { ApiClientError } from './errors';
export { isApiClientError, toApiClientError } from './errors';

// Singleton instance - THE only instance that should be used
export const apiSdk = new ApiSdk();

/**
 * Convenience methods for common HTTP verbs.
 * All methods go through apiSdk.request() for consistent handling.
 */
export const api = {
  /**
   * GET request
   */
  get: <T>(endpoint: string): Promise<T> =>
    apiSdk.request<T>(endpoint, { method: 'GET' }),

  /**
   * POST request
   */
  post: <T>(endpoint: string, body?: unknown): Promise<T> =>
    apiSdk.request<T>(endpoint, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),

  /**
   * PUT request
   */
  put: <T>(endpoint: string, body?: unknown): Promise<T> =>
    apiSdk.request<T>(endpoint, {
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),

  /**
   * PATCH request
   */
  patch: <T>(endpoint: string, body?: unknown): Promise<T> =>
    apiSdk.request<T>(endpoint, {
      method: 'PATCH',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),

  /**
   * DELETE request
   */
  delete: <T>(endpoint: string): Promise<T> =>
    apiSdk.request<T>(endpoint, { method: 'DELETE' }),
};

export default apiSdk;
